// Nemesis robots (Godot nemesis_system.gd, ENEMY_AI_DESIGN §10), the run-scoped version: the robot
// type that downs a cowboy comes back next wave as a named outlaw with a recall notice, more hull
// and a grudge. Host-only rules; a nemesis is an ordinary enemy with a flag, so it replicates
// through the normal snapshot path plus one Ev.Nemesis when it's posted.

import { Rng, hash32, secs } from './core';
import { EType } from './defs';
import type { Enemy } from './enemies';

/** Outlaw names: title × surname (bounty_board.gd, appliance-western, deadpan). Index on the wire. */
export const NEMESIS_TITLES = ['OLD', 'BIG', 'TWO-STROKE', 'COLD-CYCLE', 'DEAF', 'HALF-TON', 'RECALL-PROOF', 'LONG-HAUL'];
export const NEMESIS_SURNAMES = ['MERIDIAN', 'CAST-IRON', 'COMPRESSOR', 'SPIN-CYCLE', 'WATTS', 'DELUGE', 'FLUE', 'BALLAST'];
export function nemesisName(idx: number) {
  return `${NEMESIS_TITLES[(idx >> 3) & 7]} ${NEMESIS_SURNAMES[idx & 7]}`;
}
/** nemesis_system.gd TAUNTS (flagged there for a STORY_BIBLE pass; kept as-is). */
export const NEMESIS_TAUNTS = ['REMEMBER ME, TIN STAR?', 'YOU LEFT ME IN THE SCRAP', 'THE COMPANY PAYS DOUBLE FOR YOU', 'THIS TIME YOU STAY DOWN'];

/** Hull and shield on promotion, and how hard its hits land. */
export const NEMESIS_HP_MUL = 2.5;
export const NEMESIS_DMG_MUL = 1.3;

/** What the Game must offer the nemesis rules. */
export interface NemesisHost {
  seed: number;
  tick: number;
  enemies: Enemy[];
  emitNemesis(eid: number, nameIdx: number, type: EType, victim: number): void;
  notice(text: string): void;
  queueSpawn(type: EType, at: number, nemesis: boolean): void;
}

export class Nemesis {
  /** a grudge waiting for next wave: the robot type that downed someone, and who */
  private pending: { type: EType; victim: number } | null = null;
  /** the outlaw currently on the island (enemy id), and its name */
  active = -1;
  activeName = 0;
  private posted = 0;

  /** an outlaw that was still standing when a run ended rides again next run, harder (the grudge across nights) */
  carry: { type: EType; name: number; grudge: number } | null = null;
  private lastType: EType = EType.Swarmer;
  reset() {
    if (this.active >= 0) this.carry = { type: this.lastType, name: this.activeName, grudge: (this.carry?.grudge ?? 0) + 1 };
    this.pending = null; this.active = -1; this.posted = 0;
  }

  /** The outlaw still standing, if any (an island change clears enemies without a death). */
  private outlaw(h: NemesisHost) {
    if (this.active < 0) return null;
    const e = h.enemies.find((k) => k.id === this.active && !k.dead);
    if (!e) this.active = -1;
    return e ?? null;
  }

  /** A cowboy went down to a robot of this type (-1 = unknown or a hazard). */
  onDown(victim: number, srcType: number, h: NemesisHost) {
    if (srcType < 0 || srcType === EType.MetalBrute) return;
    const e = this.outlaw(h);
    if (e) {
      // the outlaw got someone else: it gloats
      if (e.type === srcType) h.notice(`${nemesisName(this.activeName)}: "${NEMESIS_TAUNTS[(h.tick >> 4) % NEMESIS_TAUNTS.length]}"`);
      return;
    }
    if (!this.pending) this.pending = { type: srcType as EType, victim };
  }

  /** Called by the director once per wave start, after the wave's spawn queue is composed. */
  onWaveStart(h: NemesisHost) {
    if (!this.pending && this.carry && !this.outlaw(h)) { this.pending = { type: this.carry.type, victim: 255 }; } // the grudge from last ride
    if (!this.pending || this.outlaw(h)) return;
    // one outlaw per grudge, dropped in a few seconds after the wave opens
    h.queueSpawn(this.pending.type, h.tick + secs(4), true);
  }

  /** The director spawned the queued outlaw: promote it and post the notice. */
  promote(e: Enemy, h: NemesisHost) {
    const g = this.pending;
    this.pending = null;
    const rng = new Rng(hash32(h.seed, 0x6e6d, this.posted++));
    const carried = this.carry && this.carry.type === e.type ? this.carry : null;
    this.activeName = carried ? carried.name : rng.int(0, 63);
    this.active = e.id; this.lastType = e.type;
    e.nemesis = 1;
    const mul = NEMESIS_HP_MUL * (carried ? 1 + 0.25 * carried.grudge : 1);
    if (carried) { h.notice(`${nemesisName(carried.name)} RIDES AGAIN. GRUDGE ${carried.grudge + 1}.`); this.carry = null; }
    e.hp *= mul; e.maxHp *= mul;
    e.shield *= NEMESIS_HP_MUL; e.maxShield *= NEMESIS_HP_MUL;
    h.emitNemesis(e.id, this.activeName, e.type, g ? g.victim : 255);
  }

  /** Any robot died: was it the outlaw? */
  onDeath(e: Enemy, h: NemesisHost) {
    if (e.id !== this.active) return;
    h.notice(`${nemesisName(this.activeName)} SCRAPPED. REWARD: YOUR OUTSTANDING BALANCE.`);
    this.active = -1;
  }
}
